import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtAZN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BetControl } from "@/components/game/BetControl";
import { PenaltyPitch } from "./PenaltyPitch";
import { sfx } from "@/lib/sound";

const MULTS = [1.92, 3.84, 7.68, 15.36, 30.72];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const PenaltyGame = ({ mode }) => {
  const { updateUser } = useAuth();
  const [g, setG] = useState(null);
  const [bet, setBet] = useState(1);
  const [busy, setBusy] = useState(false);
  const [anim, setAnim] = useState({ shot: null, keeper: null, result: null });
  const active = g?.status === "active";

  useEffect(() => { api.get("/penalty/active").then(({ data }) => data && setG(data)); }, []);

  const sync = (data) => updateUser({ balance: data.balance, demo_balance: data.demo_balance });

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/penalty/start", { bet, mode });
      setG(data); sync(data); setAnim({ shot: null, keeper: null, result: null }); sfx.whistle();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const shoot = async (zone) => {
    setBusy(true);
    try {
      const { data } = await api.post("/penalty/shoot", { game_id: g.game_id, zone });
      sfx.kick();
      setAnim({ shot: zone, keeper: data.keeper_zone, result: null });
      await sleep(650);
      setAnim((a) => ({ ...a, result: data.goal ? "goal" : "save" }));
      (data.goal ? sfx.cheer : sfx.groan)();
      await sleep(1200);
      setG(data); sync(data);
      if (data.status === "lost") toast.error("Qapıçı xilas etdi. Mərc uduzuldu");
      if (data.status === "won") toast.success(`Bütün qollar! +${fmtAZN(data.win)}`);
      if (data.status === "active") setAnim({ shot: null, keeper: null, result: null });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const cashout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/penalty/cashout", { game_id: g.game_id });
      setG(data); sync(data); sfx.cashout(); toast.success(`Qazanc: +${fmtAZN(data.win)}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const round = g?.round || 0;
  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6" data-testid="penalty-game">
      <div className="space-y-4">
        <div className="grid grid-cols-5 gap-2" data-testid="penalty-ladder">
          {MULTS.map((x, i) => (
            <div key={i} className={`rounded-xl py-2 text-center text-xs sm:text-sm font-display font-bold border transition-colors ${i < round ? "bg-[#D4AF37] text-black border-[#FFD700]" : i === round && active ? "border-[#D4AF37] text-[#FFD700]" : "border-white/10 text-zinc-500 bg-black/40"}`}>x{x}</div>
          ))}
        </div>
        <PenaltyPitch shot={anim.shot} keeper={anim.keeper} result={anim.result} canAim={active && !busy} onAim={shoot} />
        <p className="text-center text-sm text-zinc-400">{active ? "Zərbə üçün qapıda bir nöqtə seçin" : "Mərc edin və penaltiyə başlayın"}</p>
      </div>
      <div className="rounded-3xl border border-white/10 bg-[#111]/90 backdrop-blur-xl p-6 space-y-6 h-fit">
        <div><p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Mərc</p><BetControl value={bet} onChange={setBet} disabled={active} testid="penalty-bet" /></div>
        {active && (
          <div className="rounded-xl bg-black/40 p-4 text-center">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Qol: {round}/5 · Cari qazanc</p>
            <p className="font-display font-black text-2xl gold-text" data-testid="penalty-current-win">{fmtAZN(round ? g.bet * g.multiplier : 0)}</p>
          </div>
        )}
        {active ? (
          <button disabled={busy || round === 0} onClick={cashout} className="btn-gold w-full rounded-full py-4 font-display font-bold" data-testid="penalty-cashout-btn">Götür</button>
        ) : (
          <button disabled={busy} onClick={start} className="btn-gold w-full rounded-full py-4 font-display font-bold" data-testid="penalty-start-btn">Oyuna başla</button>
        )}
      </div>
    </div>
  );
};
